import { useRouter } from 'next/router';
import React, { useContext, useMemo, useState, useCallback } from 'react';
import { booleanFilter } from '~/shared/utils';
import { CATALOG_URLS, MEDIATHEK_URLS } from '~/core/urls/urls';
import { useMembershipContext } from '~/shared/contexts/MembershipContext';
import { useIntl } from 'react-intl';
import {
    Breadcrumb,
    getParentBreadcrumbs,
    useGetBreadcrumbsContentIdFromQuery,
    useGetBreadcrumbsData
} from './utils';

const BreadcrumbsContext = React.createContext<{
    breadcrumbs: Array<Breadcrumb>;
    lastParentBreadcrumb: Breadcrumb | undefined;
    setBreadcrumbOverride: (override: UseBreadcrumbsPayload['overrideBreadcrumbs']) => void;
}>({
    breadcrumbs: [],
    lastParentBreadcrumb: undefined,
    setBreadcrumbOverride: () => {}
});

type UseBreadcrumbsPayload = {
    overrideBreadcrumbs?:
        | {
              mode?: 'override' | 'append';
              breadcrumbs: Array<
                  Breadcrumb & {
                      level: number;
                  }
              >;
          }
        | undefined;
};

export const useBreadcrumbsContext = () => useContext(BreadcrumbsContext);

export const BreadcrumbsProvider = ({ children }: { children: React.ReactNode }) => {
    const router = useRouter();
    const { locale, formatMessage } = useIntl();
    const { isMember } = useMembershipContext();
    const [override, setOverride] = useState<UseBreadcrumbsPayload['overrideBreadcrumbs'] | null>(
        null
    );

    // URLS that should be checked for contentId and displayed as breadcrumbs
    const contentId = useGetBreadcrumbsContentIdFromQuery([
        MEDIATHEK_URLS.content,
        MEDIATHEK_URLS.contentWithVideo,
        CATALOG_URLS.handoutsCoursebook,
        MEDIATHEK_URLS.content
    ]);

    const { data, parentData } = useGetBreadcrumbsData(contentId);

    const {
        breadcrumbs,
        parentBreadcrumbs
    }: { breadcrumbs: Array<Breadcrumb>; parentBreadcrumbs: Array<Breadcrumb> } = useMemo(() => {
        if (!data && !parentData) {
            return { breadcrumbs: [], parentBreadcrumbs: [] };
        }
        const parentBreadcrumbs = getParentBreadcrumbs({
            content: data ?? null,
            parentContent: parentData ?? null,
            isMember,
            formatMessage,
            locale
        });

        if (parentBreadcrumbs.length === 0) {
            return { breadcrumbs: [], parentBreadcrumbs: [] };
        }
        return {
            breadcrumbs: [
                {
                    label: formatMessage({ id: 'common.header.home-site' }),
                    href: CATALOG_URLS.base
                },
                ...parentBreadcrumbs,
                data?.title ? { label: data.title, href: router.asPath } : undefined
            ].filter(booleanFilter),
            parentBreadcrumbs: parentBreadcrumbs
        };
    }, [data, parentData, router.asPath, isMember, locale]);

    const finalBreadcrumbs = useMemo(() => {
        if (!override) {
            return breadcrumbs;
        }

        const { mode, breadcrumbs: overrideBreadcrumbs } = override;

        if (mode === 'override') {
            return overrideBreadcrumbs;
        }

        const overrideMap = new Map(overrideBreadcrumbs.map((bc) => [bc.level, bc]));
        return breadcrumbs.map((bc, index) => {
            const positionFromEnd = breadcrumbs.length - 1 - index;
            return overrideMap.get(positionFromEnd) || bc;
        });
    }, [breadcrumbs, override]);

    const setBreadcrumbOverride = useCallback(
        (newOverride: UseBreadcrumbsPayload['overrideBreadcrumbs']) => {
            setOverride(newOverride);
        },
        []
    );

    return (
        <BreadcrumbsContext.Provider
            value={{
                breadcrumbs: finalBreadcrumbs,
                lastParentBreadcrumb: parentBreadcrumbs.at(-1),
                setBreadcrumbOverride
            }}
        >
            {children}
        </BreadcrumbsContext.Provider>
    );
};
